import { useState, type ReactNode } from 'react';
import { format } from 'date-fns';
import { Check, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ProviderBadge } from '@/components/shared/automation-reply-badges';
import {
	FilterField,
	FiltersPopover,
	SettingsSelect,
	SettingsTableRows,
	StatusTag,
	type SettingsColumn
} from '@/modules/settings';
import { isAfterTrigger, type RuleOutcome, type RunRecord, type RunStatus } from '@/types/rules.types';
import { ALL, type RulesActivityFilters } from '../../hooks/use-rules-activity-filters';
import { useTicketRules, useTicketRulesCatalog } from '../../hooks/use-ticket-rules';
import { useTicketRuleRun, useTicketRuleRuns } from '../../hooks/use-ticket-rule-runs';
import { useStepLabels } from '../../hooks/use-step-labels';
import { formatMs, OUTCOME_RESULT, RUN_STATUS_META, runDecider, runResult } from '../../lib/labels';
import { RunTrace } from '../run-trace';
import { RULES_ACTIVITY_COLUMNS } from './rules-activity-columns';


const OUTCOME_OPTIONS = [
	{ value: ALL, label: 'Any outcome' },
	...(Object.keys(OUTCOME_RESULT) as RuleOutcome[]).map((value) => ({ value, label: OUTCOME_RESULT[value].done }))
];
const STATUS_OPTIONS = [
	{ value: ALL, label: 'Any status' },
	...(Object.keys(RUN_STATUS_META) as RunStatus[]).map((value) => ({ value, label: RUN_STATUS_META[value].label }))
];

/** Header tools: the Filters popover with the playbook, outcome and status. */
export function RulesActivityTools({ filters }: { filters: RulesActivityFilters }) {
	const { data: rules = [] } = useTicketRules();
	return (
		<FiltersPopover activeCount={filters.activeCount}>
			<FilterField label='Playbook'>
				<SettingsSelect
					value={filters.ruleId}
					onValueChange={filters.setRuleId}
					options={[{ value: ALL, label: 'Any playbook' }, ...rules.map((rule) => ({ value: rule.id, label: rule.name }))]}
					aria-label='Playbook'
				/>
			</FilterField>
			<FilterField label='What happened'>
				<SettingsSelect
					value={filters.outcome}
					onValueChange={(value) => filters.setOutcome(value as RuleOutcome | typeof ALL)}
					options={OUTCOME_OPTIONS}
					aria-label='What happened'
				/>
			</FilterField>
			<FilterField label='Status'>
				<SettingsSelect
					value={filters.status}
					onValueChange={(value) => filters.setStatus(value as RunStatus | typeof ALL)}
					options={STATUS_OPTIONS}
					aria-label='Status'
				/>
			</FilterField>
		</FiltersPopover>
	);
}

const CELLS: Record<string, (run: RunRecord) => ReactNode> = {
	at: (run) => <span className='truncate tabular-nums'>{format(new Date(run.createdAt), 'd MMM, HH:mm:ss')}</span>,
	ticket: (run) => <span className='truncate font-mono text-[12px]'>#{run.ticketId}</span>,
	where: (run) => (
		<span className='flex min-w-0 items-center gap-1.5'>
			<ProviderBadge provider={run.provider} />
			<span className='truncate capitalize text-gray-900'>· {run.channel}</span>
		</span>
	),
	rule: (run) => (
		<span className='truncate'>
			{runDecider(run) ?? <span className='text-gray-800'>{isAfterTrigger(run.trigger) ? 'No playbook matched' : 'No playbook decided'}</span>}
		</span>
	),
	outcome: (run) => (
		<span className='flex flex-wrap items-center gap-1'>
			<StatusTag tone={runResult(run).tone}>{runResult(run).done}</StatusTag>
			{run.status !== 'completed' && <StatusTag tone={RUN_STATUS_META[run.status].tone}>{RUN_STATUS_META[run.status].label}</StatusTag>}
		</span>
	),
	reply: (run) =>
		run.reply?.sent ? (
			<span className='inline-flex items-center gap-1 text-green-500'>
				<Check className='size-3.5' aria-hidden />
				Yes
			</span>
		) : (
			<span className='inline-flex items-center gap-1 text-gray-800'>
				<Minus className='size-3.5' aria-hidden />
				No
			</span>
		),
	ms: (run) => <span className='tabular-nums'>{formatMs(run.timings.totalMs)}</span>
};

const COLUMNS: SettingsColumn<RunRecord>[] = RULES_ACTIVITY_COLUMNS.map((head) => ({ ...head, cell: CELLS[head.id] }));

/** The sheet a row opens: the full trace, actions, facts, timings and any error. */
function RunSheet({ row, onClose }: { row: RunRecord | undefined; onClose: () => void }) {
	const { data: catalog } = useTicketRulesCatalog();
	const { data: fresh } = useTicketRuleRun(row?.id ?? '');
	const run = fresh ?? row;
	const stepLabel = useStepLabels(catalog);
	return (
		<Sheet open={row !== undefined} onOpenChange={(open) => !open && onClose()}>
			<SheetContent className='w-[520px] overflow-y-auto sm:max-w-[520px]'>
				<SheetHeader className='px-5 pt-5'>
					<SheetTitle className='text-[15px]'>{run ? `Ticket #${run.ticketId}` : 'Run'}</SheetTitle>
					<SheetDescription className='text-[12px]'>
						{run ? `${format(new Date(run.createdAt), 'd MMM yyyy, HH:mm:ss')} · ${run.channel}` : ''}
					</SheetDescription>
				</SheetHeader>
				{run && (
					<div className='px-5 pb-6'>
						<RunTrace run={run} catalog={catalog} stepLabel={stepLabel} detailed />
					</div>
				)}
			</SheetContent>
		</Sheet>
	);
}

/**
 * Every message the playbooks looked at for the brand, newest first: which playbook decided, what
 * happened, whether a reply went out and how long it took. A row opens the step-by-step trace.
 */
export function RulesActivity({ filters }: { filters: RulesActivityFilters }) {
	const { runs, isLoading, isError, loadMoreFailed, refetch, hasMore, loadingMore, loadMore } = useTicketRuleRuns(filters.filter);
	const [openId, setOpenId] = useState<string | null>(null);
	const open = runs.find((run) => run.id === openId);

	// A failed "Load more" keeps the rows already shown; only a failed first page replaces them.
	if (isError && runs.length === 0) {
		return (
			<div className='flex flex-col items-center gap-3 py-12'>
				<p className='text-[13px] text-gray-900'>The activity could not be loaded.</p>
				<Button variant='outline' size='sm' onClick={() => void refetch()}>
					Retry
				</Button>
			</div>
		);
	}

	return (
		<div>
			<SettingsTableRows
				columns={COLUMNS}
				rows={runs}
				rowKey={(run) => run.id}
				isLoading={isLoading}
				onRowClick={(run) => setOpenId(run.id)}
				empty={
					filters.activeCount > 0
						? 'Nothing matches these filters.'
						: 'Nothing yet. Once a playbook is Live or Watching, every message it looks at shows here, step by step.'
				}
			/>
			{hasMore && (
				<div className='flex justify-center py-4'>
					<Button variant='outline' size='sm' onClick={loadMore} disabled={loadingMore} className='rounded-full'>
						{loadingMore ? 'Loading…' : loadMoreFailed ? 'Could not load more. Retry' : 'Load more'}
					</Button>
				</div>
			)}
			<RunSheet row={open} onClose={() => setOpenId(null)} />
		</div>
	);
}
